import { ref, watch } from "vue";
import { defineStore } from "pinia";

import { $http } from "@/plugins/api";
import { $userPrefs } from "@/plugins/userPrefs";

import { ACCOUNT_TYPE } from "@/constants";
import { getApiClientId, getApiClientUrl } from "@/config/runtimeConfig";

// --- PKCE helpers (unchanged from the Vuex module) --------------------------
function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  const base64 = btoa(binary);
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function generateCodeVerifier(length = 96): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);

  return toBase64Url(bytes)
    .replace(/[^A-Za-z0-9._~-]/g, "")
    .slice(0, 128);
}

async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const data = new TextEncoder().encode(codeVerifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return toBase64Url(new Uint8Array(digest));
}

// --- Loose types mirroring the untyped Vuex contract ------------------------
type Token = { access_token: string; refresh_token?: string } | null;
type AuthUser =
  | ({
      uuid?: string;
      id?: string;
      accountname?: string;
      name?: string;
      organisation?: string;
      organisation_uuid?: string;
      organisationUuid?: string;
      email?: string;
      role?: string;
      roles?: string[];
      level?: string;
      products?: string;
      expired?: number;
      type_name?: string;
      twoFactorAuthEnabled?: boolean;
      enabled_2fa?: boolean;
    } & Record<string, unknown>)
  | null;
type Selected =
  | ({
      uuid?: string;
      id?: string;
      name?: string;
      email?: string;
      type_name?: string;
      allCustomers?: boolean;
      roles?: string[];
    } & Record<string, unknown>)
  | null;

interface PersistedAuth {
  selectedAccount?: Selected;
  selectedMsp?: Selected;
  selectedCustomer?: Selected;
  selectedDomain?: Selected;
  selectedUser?: Selected;
  token?: Token;
  role?: unknown;
  organisations?: unknown;
  authUser?: AuthUser;
  mspId?: unknown;
  backupCodes?: unknown;
  permissions?: unknown;
  callbackID?: unknown;
  ssoEnabled?: boolean;
  ssoLogin?: boolean;
}

// Persist under a dedicated key. The legacy `vuex` key (written by
// vuex-persister for the whole root state) is read once on init so sessions
// created before the Pinia migration are not signed out on deploy.
const AUTH_PERSIST_KEY = "authentication";
const LEGACY_VUEX_KEY = "vuex";

const readPersisted = (): PersistedAuth | null => {
  try {
    const current = localStorage.getItem(AUTH_PERSIST_KEY);
    if (current) return JSON.parse(current) as PersistedAuth;

    const legacy = localStorage.getItem(LEGACY_VUEX_KEY);
    if (legacy) {
      const parsed = JSON.parse(legacy) as { authentication?: PersistedAuth };
      return parsed?.authentication ?? null;
    }
  } catch {
    // Corrupt storage — start clean rather than crash the app.
  }
  return null;
};

export const useAuthenticationStore = defineStore("authentication", () => {
  const persisted = readPersisted();

  const selectedAccount = ref<Selected>(persisted?.selectedAccount ?? null);
  const selectedMsp = ref<Selected>(persisted?.selectedMsp ?? null);
  const selectedCustomer = ref<Selected>(persisted?.selectedCustomer ?? null);
  const selectedDomain = ref<Selected>(persisted?.selectedDomain ?? null);
  const selectedUser = ref<Selected>(persisted?.selectedUser ?? null);
  const token = ref<Token>(persisted?.token ?? null);
  const role = ref<unknown>(persisted?.role ?? null);
  const organisations = ref<unknown>(persisted?.organisations ?? null);
  const authUser = ref<AuthUser>(persisted?.authUser ?? null);
  const mspId = ref<unknown>(persisted?.mspId ?? null);
  const backupCodes = ref<unknown>(persisted?.backupCodes ?? null);
  const permissions = ref<unknown>(persisted?.permissions ?? null);
  const callbackID = ref<unknown>(persisted?.callbackID ?? null);
  // ssoEnabled/ssoLogin had no external getter consumers; kept internal for
  // parity with the Vuex module (the ssoEnabled/ssoLogin *actions* are exposed).
  const ssoEnabledState = ref<boolean>(persisted?.ssoEnabled ?? false);
  const ssoLoginState = ref<boolean>(persisted?.ssoLogin ?? false);

  // --- Internal setters (were Vuex mutations) -------------------------------
  const setAuthUser = (payload: AuthUser) => (authUser.value = payload);
  const setToken = (payload: Token) => (token.value = payload);
  const setOrganisations = (payload: unknown) =>
    (organisations.value = payload);
  const setSelectedAccount = (payload: Selected) =>
    (selectedAccount.value = payload);
  const setSelectedCustomer = (payload: Selected) =>
    (selectedCustomer.value = payload);
  const setSelectedDomain = (payload: Selected) =>
    (selectedDomain.value = payload);
  const setMspUser = (payload: Selected) => (selectedMsp.value = payload);
  const setBackupCodes = (payload: unknown) => (backupCodes.value = payload);
  const set2Fa = (enabled: boolean) => {
    if (authUser.value) authUser.value.twoFactorAuthEnabled = enabled;
  };
  const setUser = (payload: {
    name?: string;
    email?: string;
    id?: string;
    allCustomers?: boolean;
  }) => {
    selectedUser.value = {
      accountname: payload.name,
      name: payload.name,
      email: payload.email,
      id: payload.id,
      allCustomers: payload.allCustomers,
    };
  };

  const buildAuthUser = (
    jwtPayload: Record<string, unknown>,
    authuser: Record<string, unknown>,
    organisation: Record<string, unknown>,
  ): AuthUser =>
    ({
      uuid: authuser.uuid,
      id: jwtPayload["sub"],
      accountname: (authuser.name as string) || "",
      organisation: (organisation.name as string) || "",
      organisation_uuid: (organisation.uuid as string) || "",
      email: (authuser.email as string) || "",
      twoFactorAuthEnabled: authuser.enabled_2fa,
      expired: jwtPayload["exp"],
      role: (authuser.roles as string[])?.[0] || "",
      level: (organisation.type_name as string)?.toLowerCase() || "customer",
      products: ((organisation.services as { uuid: string }[]) ?? [])
        .map((s) => s.uuid)
        .join(","),
    }) as AuthUser;

  const decodeAccessToken = (accessToken: string) => {
    const tokens = accessToken.split(".");
    const jwtPayload = JSON.parse(atob(tokens[1])) as Record<string, unknown>;
    return {
      jwtPayload,
      authuser: (jwtPayload["user"] || {}) as Record<string, unknown>,
      organisation: (jwtPayload["organisation"] || {}) as Record<
        string,
        unknown
      >,
    };
  };

  // --- Actions (were Vuex actions) ------------------------------------------
  const ssoEnabled = async (email: string) => {
    const response = await $http.get(
      `${getApiClientUrl()}/v1/idp/sso/saml/enabled/${email}`,
    );
    ssoEnabledState.value = response.data;
    return response;
  };

  const ssoLogin = async (email: string) => {
    const response = await $http.get(
      `${getApiClientUrl()}/v1/idp/sso/saml/login/${email}`,
    );
    ssoLoginState.value = response.data;
    return response;
  };

  const refreshToken = (accessToken: { access_token: string }) => {
    const { jwtPayload, authuser, organisation } = decodeAccessToken(
      accessToken.access_token,
    );

    setToken(accessToken);
    setAuthUser(buildAuthUser(jwtPayload, authuser, organisation));

    $userPrefs.updateUserPrefs({ token: accessToken.access_token });
  };

  const logUserOut = async () => {
    return await $http.post(`${getApiClientUrl()}/v1/idp/logout`);
  };

  const clearAuthCache = () => {
    selectedAccount.value = null;
    selectedMsp.value = null;
    selectedCustomer.value = null;
    selectedDomain.value = null;
    selectedUser.value = null;
    token.value = null;
    role.value = null;
    organisations.value = null;
    authUser.value = null;
    mspId.value = null;
    backupCodes.value = null;
    permissions.value = null;
    callbackID.value = null;
    ssoEnabledState.value = false;
    ssoLoginState.value = false;
  };

  const setCustomer = async (customer: Selected) =>
    setSelectedCustomer(customer);
  const setDomain = async (domain: Selected) => setSelectedDomain(domain);
  const setMspUserAction = async (msp: Selected) => setMspUser(msp);
  const setMspId = async (id: unknown) => (mspId.value = id);
  const setRole = async (value: unknown) => (role.value = value);
  const setTokenAction = async (value: Token) => setToken(value);
  const setUserAction = async (user: Parameters<typeof setUser>[0]) =>
    setUser(user);

  const enable2fa = async (code: {
    id: string;
    secret: string;
    code: string;
  }) => {
    const response = await $http.post(
      `${getApiClientUrl()}/v1/idp/users/${code.id}/2fa`,
      { secret: code.secret, code: code.code },
      { _hasCustomErrorHandling: true },
    );
    const is2fa = response.data.backup_codes ? true : false;
    $userPrefs.updateUserPrefs({ twoFactorAuthEnabled: is2fa });

    set2Fa(is2fa);
    setBackupCodes(response.data.backup_codes);
  };

  const disable2fa = async (uuid: string) => {
    const response = await $http.delete(
      `${getApiClientUrl()}/v1/idp/users/${uuid}/2fa`,
    );
    const isDisabled = response.data.uuid ? false : true;
    $userPrefs.updateUserPrefs({ twoFactorAuthEnabled: isDisabled });

    set2Fa(isDisabled);
    setBackupCodes([]);
  };

  const ssoToken = async (accessToken: { access_token: string }) => {
    const { jwtPayload, authuser, organisation } = decodeAccessToken(
      accessToken.access_token,
    );

    setToken(accessToken);
    setAuthUser(buildAuthUser(jwtPayload, authuser, organisation));
    $userPrefs.updateUserPrefs({ token: accessToken.access_token });

    setOrganisations(organisation);
    setSelectedAccount(organisation);

    if (organisation.type_name === ACCOUNT_TYPE.MSP) {
      setMspUser({
        id: authuser.uuid,
        name: authuser.name,
        email: (authuser.email as string) || "",
        roles: authuser.roles || [],
      } as Selected);
    }

    if (organisation.type_name === ACCOUNT_TYPE.CUSTOMER) {
      setSelectedCustomer({
        uuid: organisation.uuid,
        name: organisation.name,
        allCustomers: false,
      } as Selected);
    }
    // NB: the Vuex module also had `Domain`/`User` branches keyed off
    // ACCOUNT_TYPE.DOMAIN/USER, but those keys don't exist in ACCOUNT_TYPE, so
    // the branches were dead. Dropped during the Pinia migration (no behaviour
    // change).
  };

  const requestToken = async (user: {
    username: string;
    password: string;
    code?: string;
  }) => {
    const code_verifier = generateCodeVerifier();
    const code_challenge = await generateCodeChallenge(code_verifier);
    const client_id = getApiClientId();

    let params: Record<string, string | undefined> = {
      client_id,
      code_verifier,
      code_challenge,
      redirect_uri: `${getApiClientUrl()}/idp/oauth/callback`,
    };

    if (user["code"]) {
      params = { ...params, code: user["code"] };
    }

    const auth = { username: user["username"], password: user["password"] };

    const response = await $http.get(
      `${getApiClientUrl()}/v1/idp/oauth/authorize`,
      { params, auth },
    );

    setToken(response.data);

    const { jwtPayload, authuser, organisation } = decodeAccessToken(
      response.data.access_token,
    );

    setOrganisations(organisation);
    setAuthUser(buildAuthUser(jwtPayload, authuser, organisation));
    setSelectedAccount(organisation);

    if (organisation.type_name === ACCOUNT_TYPE.MSP) {
      setMspUser({
        id: authuser.uuid,
        name: organisation.name,
        email: (authuser.email as string) || "",
        roles: authuser.roles || [],
      } as Selected);
    }

    if (organisation.type_name === ACCOUNT_TYPE.CUSTOMER) {
      setSelectedCustomer({
        uuid: organisation.uuid,
        name: organisation.name,
        allCustomers: false,
      } as Selected);
    }
    // Domain/User branches dropped — see note in ssoToken (dead code).

    return response;
  };

  const forgotPassword = async (email: unknown) => {
    const response = await $http.post(
      `${getApiClientUrl()}/v1/idp/password/forgot`,
      email,
    );
    return response.data;
  };

  // --- Persistence: mirror vuex-persister (write whole state on change) -----
  watch(
    [
      selectedAccount,
      selectedMsp,
      selectedCustomer,
      selectedDomain,
      selectedUser,
      token,
      role,
      organisations,
      authUser,
      mspId,
      backupCodes,
      permissions,
      callbackID,
      ssoEnabledState,
      ssoLoginState,
    ],
    () => {
      try {
        const snapshot: PersistedAuth = {
          selectedAccount: selectedAccount.value,
          selectedMsp: selectedMsp.value,
          selectedCustomer: selectedCustomer.value,
          selectedDomain: selectedDomain.value,
          selectedUser: selectedUser.value,
          token: token.value,
          role: role.value,
          organisations: organisations.value,
          authUser: authUser.value,
          mspId: mspId.value,
          backupCodes: backupCodes.value,
          permissions: permissions.value,
          callbackID: callbackID.value,
          ssoEnabled: ssoEnabledState.value,
          ssoLogin: ssoLoginState.value,
        };
        localStorage.setItem(AUTH_PERSIST_KEY, JSON.stringify(snapshot));
      } catch {
        // Storage full / unavailable — non-fatal.
      }
    },
    { deep: true },
  );

  return {
    // state
    selectedAccount,
    selectedMsp,
    selectedCustomer,
    selectedDomain,
    selectedUser,
    token,
    role,
    organisations,
    authUser,
    mspId,
    backupCodes,
    permissions,
    callbackID,
    // actions
    ssoEnabled,
    ssoLogin,
    refreshToken,
    logUserOut,
    clearAuthCache,
    setToken: setTokenAction,
    setCustomer,
    setDomain,
    setUser: setUserAction,
    setMspUser: setMspUserAction,
    setMspId,
    setRole,
    enable2fa,
    disable2fa,
    ssoToken,
    requestToken,
    forgotPassword,
  };
});
